import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { CheckCircle2, AlertTriangle, Info, X } from 'lucide-react'

export type ToastVariant = 'success' | 'error' | 'info'

export interface ToastOptions {
  title: string
  description?: string
  variant?: ToastVariant
  /** Botão de ação (ex.: "Ver SDD", "Tentar novamente") */
  action?: { label: string; onClick: () => void }
  /** Duração em ms; erros ficam mais tempo por padrão. 0 = não fecha sozinho */
  durationMs?: number
}

interface ToastItem extends Required<Pick<ToastOptions, 'title' | 'variant'>> {
  id: number
  description?: string
  action?: { label: string; onClick: () => void }
}

interface ToastContextValue {
  toast: (options: ToastOptions) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const VARIANT_STYLES: Record<ToastVariant, { box: string; icon: React.ReactNode }> = {
  success: {
    box: 'border-emerald-300 dark:border-emerald-700',
    icon: <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" aria-hidden="true" />,
  },
  error: {
    box: 'border-red-300 dark:border-red-700',
    icon: <AlertTriangle className="w-5 h-5 text-red-500 shrink-0" aria-hidden="true" />,
  },
  info: {
    box: 'border-agent/40',
    icon: <Info className="w-5 h-5 text-agent shrink-0" aria-hidden="true" />,
  },
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const nextId = useRef(1)
  const timers = useRef<Map<number, number>>(new Map())

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
    const timer = timers.current.get(id)
    if (timer) {
      window.clearTimeout(timer)
      timers.current.delete(id)
    }
  }, [])

  const toast = useCallback(
    (options: ToastOptions) => {
      const id = nextId.current++
      const variant = options.variant ?? 'info'
      const duration = options.durationMs ?? (variant === 'error' ? 8000 : 5000)
      setToasts((prev) => [
        ...prev.slice(-3), // no máximo 4 toasts empilhados
        { id, title: options.title, description: options.description, variant, action: options.action },
      ])
      if (duration > 0) {
        timers.current.set(id, window.setTimeout(() => dismiss(id), duration))
      }
    },
    [dismiss]
  )

  const value = useMemo(() => ({ toast }), [toast])

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div
          aria-live="polite"
          aria-atomic="false"
          className="fixed bottom-6 right-6 z-[100] flex flex-col gap-2 w-[min(22rem,calc(100vw-3rem))]"
        >
          {toasts.map((t) => {
            const style = VARIANT_STYLES[t.variant]
            return (
              <div
                key={t.id}
                role="status"
                className={`flex items-start gap-3 rounded-xl border bg-white dark:bg-gray-900 shadow-lg px-4 py-3 ${style.box}`}
              >
                {style.icon}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{t.title}</p>
                  {t.description && (
                    <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-300">{t.description}</p>
                  )}
                  {t.action && (
                    <button
                      type="button"
                      onClick={() => {
                        t.action?.onClick()
                        dismiss(t.id)
                      }}
                      className="mt-2 text-xs font-semibold text-agent hover:underline focus:outline-none focus:ring-2 focus:ring-agent rounded"
                    >
                      {t.action.label}
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => dismiss(t.id)}
                  aria-label="Fechar aviso"
                  className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-agent"
                >
                  <X className="w-3.5 h-3.5" aria-hidden="true" />
                </button>
              </div>
            )
          })}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  )
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    throw new Error('useToast deve ser usado dentro de <ToastProvider>')
  }
  return ctx
}
